import React, { useState, useEffect } from 'react';
import { useCart } from '../contexts/CartContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Skeleton,
  Alert,
  Snackbar,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Grid,
  Chip,
  RadioGroup,
  Radio,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PaymentIcon from '@mui/icons-material/Payment';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import EventIcon from '@mui/icons-material/Event';
import BusinessIcon from '@mui/icons-material/Business';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import CircularProgress from '@mui/material/CircularProgress';
import { useAuth } from '../contexts/AuthContext';
import DialogHeader from '../components/Common/DialogHeader';
import usePaymentConfig, { providerAcceptsBarion } from '../hooks/usePaymentConfig';
import BarionPaymentOption from '../components/Payment/BarionPaymentOption';

const PAYMENT_METHOD_LABELS = {
  ON_SITE_CARD: 'Helyszínen bankkártyával',
  ON_SITE_CASH: 'Helyszínen készpénzzel',
  BANK_TRANSFER: 'Online utalással (számlaszámra)',
};

const glassCard = {
  background: 'rgba(255,255,255,0.55)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  border: '1px solid rgba(255,255,255,0.8)',
  boxShadow: '0 2px 12px rgba(27,51,31,0.08)',
  borderRadius: '16px',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 24px rgba(27,51,31,0.13)',
  },
};

const formatHUF = (amount) =>
  `${(amount || 0).toLocaleString('hu-HU')} Ft`;

const getBookingPayableAmount = (booking) => {
  if (booking.paymentStatus === 'DEPOSIT_REQUESTED') return parseFloat(booking.depositAmount || 0);
  if (booking.paymentStatus === 'DEPOSIT_PAID') return Math.max(0, parseFloat(booking.finalPrice || 0) - parseFloat(booking.depositAmount || 0));
  return parseFloat(booking.finalPrice || 0);
};

const REQUIRED_FIELDS = ['lastName', 'firstName', 'email', 'phone', 'address', 'city', 'zipCode'];
const INVOICE_REQUIRED = ['invoiceName', 'invoiceAddress'];

const validateCheckoutForm = (form) => {
  const errors = {};
  REQUIRED_FIELDS.forEach((field) => {
    if (!form[field]?.trim()) errors[field] = 'Kötelező mező';
  });
  if (form.wantsInvoice) {
    INVOICE_REQUIRED.forEach((field) => {
      if (!form[field]?.trim()) errors[field] = 'Kötelező mező';
    });
  }
  return errors;
};

const CartPage = () => {
  const { cartItems, removeFromCart, clearCart } = useCart();
  const { user } = useAuth();
  const paymentConfig = usePaymentConfig();
  const navigate = useNavigate();

  const [bookingsInCart, setBookingsInCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState(1); // 1: payment method, 2: personal data
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [offlineResult, setOfflineResult] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const [checkoutForm, setCheckoutForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: '',
    city: '',
    zipCode: '',
    wantsInvoice: false,
    invoiceName: '',
    invoiceAddress: '',
    invoiceTaxNumber: '',
  });

  useEffect(() => {
    if (user) {
      setCheckoutForm((prev) => ({
        ...prev,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
      }));
    }
  }, [user]);

  useEffect(() => {
    const fetchBookingsInCart = async () => {
      if (cartItems.length === 0) {
        setBookingsInCart([]);
        setSelectedItems([]);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
          setSnackbar({ open: true, message: 'Nincs bejelentkezve.', severity: 'error' });
          setLoading(false);
          return;
        }
        // Védőháló: egy duplikált bookingId itt duplán lekérné (és a végösszegbe
        // duplán beszámítaná) ugyanazt a foglalást, ezért még a lekérés előtt
        // egyedivé tesszük a listát.
        const uniqueIds = [...new Set(cartItems)];
        const fetched = await Promise.all(
          uniqueIds.map((id) =>
            axios
              .get(`${process.env.REACT_APP_BACKEND_URL}/api/bookings/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
              })
              .then((r) => r.data.data)
          )
        );
        setBookingsInCart(fetched);
        setSelectedItems(fetched.map((b) => b.id));
      } catch (err) {
        console.error('Error fetching bookings for cart:', err);
        setSnackbar({ open: true, message: 'Hiba a kosár betöltésekor.', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchBookingsInCart();
  }, [cartItems]);

  const handleCheckboxChange = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSelectAllChange = (e) => {
    setSelectedItems(e.target.checked ? bookingsInCart.map((b) => b.id) : []);
  };

  const handleFieldChange = (field, value) => {
    setCheckoutForm((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleOpenCheckout = () => {
    if (selectedItems.length === 0) {
      setSnackbar({ open: true, message: 'Válasszon ki legalább egy elemet a fizetéshez.', severity: 'warning' });
      return;
    }
    if (hasMixedPaymentSteps) {
      setSnackbar({ open: true, message: 'Előleg és teljes/fennmaradó összeg egyszerre nem fizethető — kérjük válasszon csak azonos típusú tételeket.', severity: 'warning' });
      return;
    }
    setFormErrors({});
    setCheckoutStep(1);
    setSelectedPaymentMethod('');
    setOfflineResult(null);
    setCheckoutDialogOpen(true);
  };

  // Kalkulálja az elérhető fizetési módokat
  const getAvailablePaymentMethods = () => {
    const selected = bookingsInCart.filter(b => selectedItems.includes(b.id));
    const providerIds = [...new Set(selected.map(b => b.providerId))];
    if (providerIds.length > 1) return { isMultiProvider: true, methods: [], bankAccountNumber: null };
    const provider = selected[0]?.provider;
    const methods = (provider?.paymentMethods || []).filter(m => m !== 'BARION');
    return { isMultiProvider: false, methods, bankAccountNumber: provider?.bankAccountNumber };
  };

  // Kalkulálja a fizetési lépést
  const getPaymentStep = () => {
    const selected = bookingsInCart.filter(b => selectedItems.includes(b.id));
    if (selected.some(b => b.paymentStatus === 'DEPOSIT_REQUESTED')) return 'DEPOSIT';
    return 'FULL';
  };

  const handleInitiatePayment = async () => {
    const errors = validateCheckoutForm(checkoutForm);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setPaymentProcessing(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setSnackbar({ open: true, message: 'Nincs bejelentkezve.', severity: 'error' });
        setPaymentProcessing(false);
        return;
      }

      // Offline fizetési mód
      if (selectedPaymentMethod && selectedPaymentMethod !== 'BARION') {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/payments/initiate-offline`,
          {
            bookingIds: selectedItems,
            selectedPaymentMethod,
            paymentStep,
            checkoutDetails: checkoutForm,
          },
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
        if (response.data.success) {
          setOfflineResult(response.data);
          setCheckoutStep(3); // sikerképernyő
          clearCart();
        } else {
          setSnackbar({ open: true, message: response.data.message || 'Hiba történt.', severity: 'error' });
        }
        return;
      }

      // Barion fizetési mód (meglévő flow)
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/initiate`,
        { bookingIds: selectedItems, paymentStep, checkoutDetails: checkoutForm },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (response.data.redirectUrl) {
        setCheckoutDialogOpen(false);
        window.location.href = response.data.redirectUrl;
      } else {
        setSnackbar({ open: true, message: 'Hiba: nincs átirányítási URL.', severity: 'error' });
      }
    } catch (err) {
      console.error('Fizetés indítása hiba:', err.response?.data || err.message);
      setSnackbar({
        open: true,
        message: `Hiba a fizetés indításakor: ${err.response?.data?.message || err.message}`,
        severity: 'error',
      });
    } finally {
      setPaymentProcessing(false);
    }
  };

  const paymentStep = getPaymentStep();
  const selectedBookings = bookingsInCart.filter((b) => selectedItems.includes(b.id));
  const hasMixedPaymentSteps =
    selectedBookings.length > 1 &&
    selectedBookings.some((b) => b.paymentStatus === 'DEPOSIT_REQUESTED') &&
    selectedBookings.some((b) => b.paymentStatus !== 'DEPOSIT_REQUESTED');
  const isMultiProviderSelected =
    [...new Set(selectedBookings.map((b) => b.providerId))].length > 1;
  // Több szolgáltató foglalása egyszerre csak Barionnal fizethető (mindegyiknek fogadnia kell)
  const barionAvailableForSelection = paymentConfig.barionEnabled
    && selectedBookings.length > 0
    && selectedBookings.every((b) => providerAcceptsBarion(b.provider));
  const canCombineItems = !hasMixedPaymentSteps && (!isMultiProviderSelected || barionAvailableForSelection);
  const totalAmount = selectedBookings.reduce((sum, b) => sum + getBookingPayableAmount(b), 0);

  const allSelected =
    bookingsInCart.length > 0 && selectedItems.length === bookingsInCart.length;

  const tf = (field, label, extraProps = {}) => (
    <TextField
      fullWidth
      label={label}
      value={checkoutForm[field]}
      onChange={(e) => handleFieldChange(field, e.target.value)}
      error={!!formErrors[field]}
      helperText={formErrors[field]}
      size="small"
      {...extraProps}
    />
  );

  return (
    <Box
      sx={{
        minHeight: '80vh',
        background: 'linear-gradient(160deg, rgba(247,224,184,0.18) 0%, rgba(245,241,230,0.25) 100%)',
        py: { xs: 3, md: 5 },
        px: { xs: 2, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Fejléc */}
        <Typography
          variant="h3"
          sx={{
            fontFamily: '"Playfair Display", serif',
            color: 'primary.dark',
            fontWeight: 700,
            mb: 4,
            fontSize: { xs: '1.8rem', md: '2.4rem' },
          }}
        >
          Kosár
          {!loading && bookingsInCart.length > 0 && (
            <Typography
              component="span"
              sx={{ ml: 1.5, fontSize: '1rem', color: 'text.secondary', fontFamily: '"Inter", sans-serif', fontWeight: 400 }}
            >
              ({bookingsInCart.length} tétel)
            </Typography>
          )}
        </Typography>

        {/* Betöltés */}
        {loading && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              {[1, 2, 3].map((i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  height={100}
                  sx={{ mb: 2, borderRadius: '16px' }}
                />
              ))}
            </Grid>
            <Grid item xs={12} md={4}>
              <Skeleton variant="rounded" height={260} sx={{ borderRadius: '16px' }} />
            </Grid>
          </Grid>
        )}

        {/* Üres kosár */}
        {!loading && bookingsInCart.length === 0 && (
          <Box
            sx={{
              ...glassCard,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 8,
              px: 4,
              textAlign: 'center',
            }}
          >
            <ShoppingCartIcon sx={{ fontSize: 72, color: 'primary.light', mb: 2, opacity: 0.7 }} />
            <Typography variant="h5" sx={{ fontFamily: '"Playfair Display", serif', color: 'primary.dark', mb: 1 }}>
              A kosár üres
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Még nem adott hozzá foglalást a kosárhoz.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => navigate('/services')}
              sx={{ borderRadius: '10px', px: 4 }}
            >
              Fedezze fel szolgáltatóinkat
            </Button>
          </Box>
        )}

        {/* Kosár tartalom */}
        {!loading && bookingsInCart.length > 0 && (
          <Grid container spacing={3} alignItems="flex-start">
            {/* Bal: kosár elemek */}
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {bookingsInCart.map((booking) => {
                  const isSelected = selectedItems.includes(booking.id);
                  return (
                    <Card
                      key={booking.id}
                      sx={{
                        ...glassCard,
                        outline: isSelected ? '2px solid' : 'none',
                        outlineColor: 'primary.main',
                      }}
                    >
                      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: '12px !important' }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleCheckboxChange(booking.id)}
                          sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontFamily: '"Playfair Display", serif',
                              fontWeight: 600,
                              color: 'primary.dark',
                              lineHeight: 1.3,
                            }}
                          >
                            {booking.eventType || 'Foglalás'}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                            {booking.provider?.businessName && (
                              <Chip
                                icon={<BusinessIcon sx={{ fontSize: '0.85rem !important' }} />}
                                label={booking.provider.businessName}
                                size="small"
                                sx={{ bgcolor: 'rgba(42,93,50,0.08)', color: 'primary.dark', fontSize: '0.78rem' }}
                              />
                            )}
                            {booking.eventDate && (
                              <Chip
                                icon={<EventIcon sx={{ fontSize: '0.85rem !important' }} />}
                                label={new Date(booking.eventDate).toLocaleDateString('hu-HU')}
                                size="small"
                                sx={{ bgcolor: 'rgba(237,190,95,0.12)', color: 'text.secondary', fontSize: '0.78rem' }}
                              />
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          {booking.paymentStatus === 'DEPOSIT_REQUESTED' ? (
                            <>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Előleg
                              </Typography>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'warning.dark', whiteSpace: 'nowrap' }}>
                                {formatHUF(booking.depositAmount)}
                              </Typography>
                              <Typography variant="caption" color="text.disabled">
                                Teljes: {formatHUF(booking.finalPrice)}
                              </Typography>
                            </>
                          ) : booking.paymentStatus === 'DEPOSIT_PAID' ? (
                            <>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Fennmaradó
                              </Typography>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main', whiteSpace: 'nowrap' }}>
                                {formatHUF(parseFloat(booking.finalPrice || 0) - parseFloat(booking.depositAmount || 0))}
                              </Typography>
                              <Typography variant="caption" color="text.disabled">
                                Előleg: {formatHUF(booking.depositAmount)} ✓
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main', whiteSpace: 'nowrap' }}>
                              {formatHUF(booking.finalPrice)}
                            </Typography>
                          )}
                        </Box>
                        <IconButton
                          edge="end"
                          onClick={() => removeFromCart(booking.id)}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': { color: 'error.main', bgcolor: 'rgba(211,47,47,0.06)' },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            </Grid>

            {/* Jobb: összesítő panel */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  ...glassCard,
                  position: { md: 'sticky' },
                  top: { md: 88 },
                  '&:hover': {},
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontFamily: '"Playfair Display", serif', color: 'primary.dark', mb: 2, fontWeight: 600 }}
                  >
                    Összesítő
                  </Typography>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allSelected}
                        indeterminate={selectedItems.length > 0 && !allSelected}
                        onChange={handleSelectAllChange}
                        sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }}
                      />
                    }
                    label={
                      <Typography variant="body2" color="text.secondary">
                        Összes kiválasztása ({bookingsInCart.length} tétel)
                      </Typography>
                    }
                    sx={{ mb: 2, ml: 0 }}
                  />

                  <Divider sx={{ mb: 2 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Kiválasztva:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedItems.length} / {bookingsInCart.length} tétel
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="body1" fontWeight={600} color="primary.dark">
                      Összesen:
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: 'primary.main', fontFamily: '"Playfair Display", serif' }}
                    >
                      {formatHUF(totalAmount)}
                    </Typography>
                  </Box>

                  {selectedItems.length > 1 && hasMixedPaymentSteps && (
                    <Alert severity="warning" sx={{ mb: 2, fontSize: '0.82rem' }}>
                      A kiválasztott tételek között előleg- és teljes összeg is szerepel — ezeket nem lehet egyszerre kifizetni.
                    </Alert>
                  )}
                  {selectedItems.length > 1 && isMultiProviderSelected && !hasMixedPaymentSteps && (
                    <Alert severity="warning" sx={{ mb: 2, fontSize: '0.82rem' }}>
                      A kiválasztott tételek több szolgáltatótól származnak — ezeket nem lehet egyszerre kifizetni. Kérjük, válasszon csak egy szolgáltató foglalásait.
                    </Alert>
                  )}

                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleOpenCheckout}
                    disabled={selectedItems.length === 0 || !canCombineItems || paymentProcessing}
                    startIcon={<PaymentIcon />}
                    sx={{
                      borderRadius: '10px',
                      py: 1.4,
                      background: 'linear-gradient(45deg, #2a5d32 0%, #1b331f 100%)',
                      fontWeight: 600,
                      mb: 1.5,
                    }}
                  >
                    Fizetés indítása
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    color="primary"
                    onClick={clearCart}
                    disabled={paymentProcessing}
                    sx={{ borderRadius: '10px', color: 'text.secondary', borderColor: 'divider' }}
                  >
                    Kosár ürítése
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Checkout Dialog – kétlépéses */}
      <Dialog
        open={checkoutDialogOpen}
        onClose={() => !paymentProcessing && checkoutStep !== 3 && setCheckoutDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '20px', background: 'rgba(245,241,230,0.97)' } }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader icon={<PaymentIcon />}
            title={checkoutStep === 3 ? 'Fizetés visszaigazolva' : checkoutStep === 2 ? 'Személyes adatok' : 'Fizetési mód'}
            subtitle={checkoutStep < 3 ? (paymentStep === 'DEPOSIT' ? `Előleg fizetendő: ${formatHUF(totalAmount)}` : `Fizetendő összeg: ${formatHUF(totalAmount)}`) : undefined}
          />
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
          {/* 1. lépés: Fizetési mód kiválasztása */}
          {checkoutStep === 1 && (() => {
            const { isMultiProvider, methods, bankAccountNumber } = getAvailablePaymentMethods();
            return (
              <>
                {isMultiProvider ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Több szolgáltató foglalásait csak online (Barion) fizetéssel lehet egyszerre kiegyenlíteni.
                    {!barionAvailableForSelection && ' Nem minden kiválasztott szolgáltató fogad online fizetést — kérjük, válassza ki csak egy szolgáltató foglalásait.'}
                  </Alert>
                ) : (
                  <>
                    {methods.length === 0 && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        A szolgáltató még nem állított be fizetési módot. Kérjük, vegye fel a kapcsolatot közvetlenül.
                      </Alert>
                    )}
                    <RadioGroup value={selectedPaymentMethod} onChange={(e) => setSelectedPaymentMethod(e.target.value)}>
                      {methods.map(method => (
                        <FormControlLabel
                          key={method}
                          value={method}
                          control={<Radio sx={{ color: 'primary.main' }} />}
                          label={PAYMENT_METHOD_LABELS[method] || method}
                          sx={{ mb: 1, border: '1px solid', borderColor: selectedPaymentMethod === method ? 'primary.main' : 'divider',
                            borderRadius: '8px', px: 1.5, py: 0.5, mx: 0 }}
                        />
                      ))}
                    </RadioGroup>
                    {selectedPaymentMethod === 'BANK_TRANSFER' && bankAccountNumber && (
                      <Alert severity="info" sx={{ mt: 1.5 }}>
                        <strong>Bankszámlaszám:</strong> {bankAccountNumber}<br />
                        A megerősítés után egyedi közlemény azonosítót kap.
                      </Alert>
                    )}
                  </>
                )}
                <BarionPaymentOption
                  enabled={paymentConfig.barionEnabled}
                  available={barionAvailableForSelection}
                  selected={selectedPaymentMethod === 'BARION'}
                  onSelect={() => setSelectedPaymentMethod('BARION')}
                />
              </>
            );
          })()}

          {/* 2. lépés: Személyes adatok */}
          {checkoutStep === 2 && (
            <>
              <Typography variant="h6"
                sx={{ fontFamily: '"Playfair Display", serif', color: 'primary.dark', mb: 2, fontSize: '1rem' }}>
                Személyes adatok
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>{tf('lastName', 'Vezetéknév')}</Grid>
                <Grid item xs={6}>{tf('firstName', 'Keresztnév')}</Grid>
                <Grid item xs={6}>{tf('email', 'Email', { type: 'email' })}</Grid>
                <Grid item xs={6}>{tf('phone', 'Telefonszám', { type: 'tel' })}</Grid>
              </Grid>
              <Typography variant="h6"
                sx={{ fontFamily: '"Playfair Display", serif', color: 'primary.dark', mt: 3, mb: 2, fontSize: '1rem' }}>
                Számlázási cím
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>{tf('address', 'Utca, házszám')}</Grid>
                <Grid item xs={6}>{tf('city', 'Város')}</Grid>
                <Grid item xs={6}>{tf('zipCode', 'Irányítószám')}</Grid>
              </Grid>
              <Divider sx={{ my: 3 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Fizetési mód: <strong>{PAYMENT_METHOD_LABELS[selectedPaymentMethod] || selectedPaymentMethod}</strong>
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', fontFamily: '"Playfair Display", serif' }}>
                  {formatHUF(totalAmount)}
                </Typography>
              </Box>
            </>
          )}

          {/* 3. lépés: Siker */}
          {checkoutStep === 3 && offlineResult && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" color="success.main" gutterBottom>Fizetési igény rögzítve!</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Email visszaigazolást küldtünk a megadott email címre.
              </Typography>
              {offlineResult.data?.selectedPaymentMethod === 'BANK_TRANSFER' && (
                <Box sx={{ background: 'rgba(33,150,243,0.06)', border: '1px solid rgba(33,150,243,0.2)', borderRadius: '12px', p: 2, mt: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>Átutalási adatok:</Typography>
                  <Typography variant="body2"><strong>Bankszámlaszám:</strong> {offlineResult.data.bankAccountNumber}</Typography>
                  {(offlineResult.data.bookings || []).map(b => (
                    <Box key={b.id} sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        Foglalás #{b.id} közleménye: <strong style={{ fontFamily: 'monospace', background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>{b.transferReferenceId}</strong>
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Miután a fizetés megtörtént, kérjük igazolja vissza a foglalás oldalán.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          {checkoutStep === 1 && (
            <>
              <Button onClick={() => setCheckoutDialogOpen(false)} sx={{ color: 'text.secondary' }}>Mégse</Button>
              <Button variant="contained" onClick={() => {
                if (!selectedPaymentMethod) {
                  setSnackbar({ open: true, message: 'Kérjük válasszon fizetési módot.', severity: 'warning' });
                  return;
                }
                setCheckoutStep(2);
              }}>
                Tovább
              </Button>
            </>
          )}
          {checkoutStep === 2 && (
            <>
              <Button onClick={() => setCheckoutStep(1)} disabled={paymentProcessing} sx={{ color: 'text.secondary' }}>Vissza</Button>
              <Button onClick={() => setCheckoutDialogOpen(false)} disabled={paymentProcessing} sx={{ color: 'text.secondary' }}>Mégse</Button>
              <Button variant="contained" color="primary" onClick={handleInitiatePayment}
                disabled={paymentProcessing || selectedItems.length === 0}
                startIcon={paymentProcessing ? <CircularProgress size={18} color="inherit" /> : <PaymentIcon />}
                sx={{ borderRadius: '10px', px: 3, background: 'linear-gradient(45deg, #2a5d32 0%, #1b331f 100%)', fontWeight: 600 }}>
                {paymentProcessing ? 'Feldolgozás...' : 'Megerősítés'}
              </Button>
            </>
          )}
          {checkoutStep === 3 && (
            <Button variant="contained" fullWidth onClick={() => { setCheckoutDialogOpen(false); navigate('/foglalasok'); }}>
              Foglalásaim megtekintése
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Hibajelzés Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ borderRadius: '12px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CartPage;
